import React from 'react';

const Row = ({ name, type, purpose, duration }) => (
  <tr className="border-b border-slate-100 last:border-0">
    <td className="py-2.5 pr-4 font-mono text-xs text-slate-800 align-top whitespace-nowrap">{name}</td>
    <td className="py-2.5 pr-4 text-xs text-slate-500 align-top whitespace-nowrap">{type}</td>
    <td className="py-2.5 pr-4 text-sm text-slate-600 align-top">{purpose}</td>
    <td className="py-2.5 text-xs text-slate-500 align-top whitespace-nowrap">{duration}</td>
  </tr>
);

export default function CookiePolicyPage() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="cookie-policy-page">
      <header className="bg-[#0F172A] text-white">
        <div className="max-w-3xl mx-auto px-6 py-10">
          <h1 className="text-3xl font-semibold">Cookie Policy</h1>
          <p className="mt-2 text-sm text-white/70">
            PNA Title Services LLC — TitleFlow Platform · Last updated: July 25, 2026
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-slate-800 mb-3">Our approach: essential only</h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            TitleFlow uses only <strong>strictly necessary</strong> cookies and browser storage —
            the minimum required for you to sign in securely and for the application to remember
            your interface preferences. We do <strong>not</strong> use analytics cookies,
            advertising cookies, social-media pixels, or any third-party tracking technology.
            Because everything we store is essential to operating the service, no consent is
            legally required — this page exists so you know exactly what is stored and why.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-semibold text-slate-800 mb-3">What we store</h2>
          <div className="bg-white rounded-xl border border-slate-200 p-5 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400">
                  <th className="py-2 pr-4 font-medium">Name</th>
                  <th className="py-2 pr-4 font-medium">Type</th>
                  <th className="py-2 pr-4 font-medium">Purpose</th>
                  <th className="py-2 font-medium">Duration</th>
                </tr>
              </thead>
              <tbody>
                <Row name="token" type="Local storage"
                     purpose="Your signed-in session token (JWT). Required to authenticate every request you make."
                     duration="Until logout / session expiry" />
                <Row name="user" type="Local storage"
                     purpose="Your basic profile (name, role) so the interface can render without an extra lookup."
                     duration="Until logout" />
                <Row name="access_token / refresh_token" type="HttpOnly cookie"
                     purpose="Secure session cookies used by the cookie-based sign-in variant. HttpOnly and Secure — not readable by scripts."
                     duration="1 hour / 7 days" />
                <Row name="tf-cookie-notice-ack" type="Local storage"
                     purpose="Remembers that you acknowledged this cookie notice so it is not shown again."
                     duration="Persistent (until policy changes)" />
                <Row name="UI preference keys" type="Local storage"
                     purpose="Interface preferences such as rows-per-page, dismissed banners, and collapsed dashboard sections (e.g. hrDirectoryRowsPerPage, showWelcomeBanner, opdash-collapsed-*)."
                     duration="Persistent" />
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-semibold text-slate-800 mb-3">What we do NOT use</h2>
          <ul className="list-disc pl-5 text-sm text-slate-600 leading-relaxed space-y-1">
            <li>No analytics or measurement cookies (e.g., Google Analytics).</li>
            <li>No advertising, remarketing, or cross-site tracking cookies.</li>
            <li>No social-media plugins or pixels.</li>
            <li>No third-party cookies of any kind.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-semibold text-slate-800 mb-3">Managing cookies</h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            You can clear cookies and site data at any time through your browser settings. Note
            that because everything TitleFlow stores is essential, clearing it will sign you out
            and reset your interface preferences. Blocking storage entirely will prevent you from
            signing in.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-semibold text-slate-800 mb-3">Changes & contact</h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            If our cookie usage ever changes (for example, if analytics are introduced), this page
            will be updated and the in-app cookie notice will be shown again for
            re-acknowledgment. Questions:{' '}
            <a href="mailto:support@pnatitleservicesllc.com" className="text-blue-600 hover:underline">
              support@pnatitleservicesllc.com
            </a>
          </p>
        </section>

        <div className="border-t border-slate-200 pt-6 flex items-center justify-between text-sm">
          <a href="/" className="text-blue-600 hover:text-blue-700 hover:underline">&larr; Back to TitleFlow</a>
          <a href="/privacy-policy" className="text-blue-600 hover:text-blue-700 hover:underline" data-testid="cookie-to-privacy-link">
            Privacy Policy &rarr;
          </a>
        </div>
      </main>
    </div>
  );
}
